import * as Sentry from "@sentry/react"
import { API_BASE_URL, apiClient } from "./client"
import { setCsrfToken } from "./csrf-store"
import {
  ApiRequestError,
  ExpiredTokenError,
  InvalidAuthorizationCodeError,
  InvalidResendTokenError,
  InvitationNotActionableError,
  InvitationNotFoundError,
} from "./errors"

export const VerificationStatusMap = {
  UNVERIFIED: "unverified",
  EMAIL_CODE_SENT: "email_code_sent",
  EMAIL_VERIFIED: "email_verified",
  CARD_ID_UPLOAD: "card_id_upload",
  CARD_ID_VERIFIED: "card_id_verified",
  VIDEO_UPLOAD: "video_upload",
  VIDEO_VERIFIED: "video_verified",
  ATTACHMENTS: "attachments",
  ATTACHMENTS_VERIFIED: "attachments_verified",
  RESERVATION_OF_RIGHTS: "reservation_of_rights",
  RESERVATION_OF_RIGHTS_VERIFIED: "reservation_of_rights_verified",
  BANK_INFO: "bank_info",
  BANK_INFO_VERIFIED: "bank_info_verified",
  VERIFIED: "verified",
} as const

export const SignatureServiceMap = {
  ECERT: "ecert",
  FIRME_SIMPLE: "firme_simple",
} as const

export const DocumentStatusMap = {
  IN_PROCESS: "in_process",
  SIGNED: "signed",
  REJECTED: "rejected",
} as const

export const SigneeStatusMap = {
  IN_PROCESS: "in_process",
  SIGNING: "signing",
  FAILED: "failed",
  SIGNED: "signed",
  REJECTED: "rejected",
} as const

type VerificationStatus =
  (typeof VerificationStatusMap)[keyof typeof VerificationStatusMap]
type SignatureService =
  (typeof SignatureServiceMap)[keyof typeof SignatureServiceMap]
type DocumentStatus = (typeof DocumentStatusMap)[keyof typeof DocumentStatusMap]
type SigneeStatus = (typeof SigneeStatusMap)[keyof typeof SigneeStatusMap]

export type DocumentSignee = {
  id: number
  email: string
  status: SigneeStatus
  verification: string[]
  verification_status: VerificationStatus
  created_at: string // TODO: parse as a date if needed
  updated_at: string // TODO: parse as a date if needed
}

export type SigningDocument = {
  id: number
  name: string
  service: SignatureService
  status: DocumentStatus
  created_at: string // TODO: parse as a date if needed
  updated_at: string // TODO: parse as a date if needed
}

export type EmailVerificationStatus =
  | {
      status: Extract<VerificationStatus, "unverified" | "email_verified">
      sent_at: null
      expires_at: null
    }
  | {
      status: Extract<VerificationStatus, "email_code_sent">
      sent_at: string // TODO: parse as date
      expires_at: string // TODO: parse as date
    }

export type CardIdVerificationStatus = {
  status: VerificationStatus
  front_side_url: string | null
  rear_side_url: string | null
}

export type VideoVerificationStatus = {
  status: VerificationStatus
  legend: string | null
  video_url: string | null
}

export type ReservationOfRightsStatus = {
  status: VerificationStatus
  reservation_of_rights: boolean | null
  reservation_of_rights_content: string | null
}

export type BankAccountType = "sight" | "checking" | "savings"

export type BankResponse = {
  id: number
  name: string
  code: string
  disabled: boolean
}

export type BankAccountResponse = {
  id: number
  holder_id: string
  holder_name: string
  number: string
  type: BankAccountType
  bank_id: number
  bank: BankResponse
  confirmed_at: string | null
  holder_prefilled: boolean
}

export type BankAccountStatus = {
  status: VerificationStatus
  bank_account: BankAccountResponse | null
}

export type BankAccountCreate = {
  holder_id: string
  holder_name: string
  number: string
  type: BankAccountType
  bank_id: number
}

export type GetDocumentFileResponse = {
  download_url: string
}

export type NextStepResponse = {
  next_step: string | null
  redirect_url: string | null
}

export type AttachmentItem = {
  id: number
  name: string
  mime_type: string
  download_url: string
  description: string | null
}

// Payment types
export type PaymentStatus = "pending" | "approved" | "rejected" | "cancelled"

export interface PaymentStatusResponse {
  required: boolean
  amount?: number
  status?: PaymentStatus
}

export interface ProcessPaymentRequest {
  token: string
  payment_method_id: string
  installments: number
  payer: {
    email: string
    identification: {
      type: string
      number: string
    }
  }
}

export interface ProcessPaymentResponse {
  status: PaymentStatus
  status_detail: string
  payment_id: string
}

export interface CreatePaymentPreferenceResponse {
  preference_id: string
}

export type ValidationSignee = {
  id: number
  full_name: string
  rut_masked: string
  status: SigneeStatus
  signed_at: string | null
}

export type ValidationResponse = {
  document: {
    name: string
    status: DocumentStatus
    signed_at: string | null
  }
  scanned_signee_id: number
  signees: ValidationSignee[]
  has_download: boolean
}

export type SessionResponse = {
  document_id: number
  signee_id: number
  invitation_id: number
}

export type GetTokenResponse = SessionResponse & {
  csrf_token: string
}

export const getToken = ({
  token_id,
  code,
}: {
  token_id: string
  code: string
}) =>
  apiClient
    .post<GetTokenResponse>("/token", { token_id, code })
    .then((data) => {
      setCsrfToken(data.csrf_token)
      return data
    })
    .catch((e: ApiRequestError) => {
      if (e.type === "InvitationNotFound")
        throw new InvitationNotFoundError(e.message)
      if (e.type === "ExpiredAuthorizationCode")
        throw new InvalidAuthorizationCodeError({
          message: e.message,
          resendToken: e.body.resend_token as string | undefined,
          resendTokenExpiresAt: e.body.resend_token_expires_at as
            | string
            | undefined,
          signeeEmailHint: e.body.signee_email_hint as string | undefined,
        })
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const requestNewInvitation = ({
  token_id,
  resend_token,
  email,
}: {
  token_id: string
  resend_token: string
  email: string
}) =>
  apiClient
    .post<null>(
      `/invitations/${token_id}/resend`,
      { email },
      { headers: { "X-Resend-Token": resend_token } },
    )
    .catch((e: ApiRequestError) => {
      if (e.type === "InvalidResendToken")
        throw new InvalidResendTokenError(e.message)
      if (e.type === "InvitationNotActionable")
        throw new InvitationNotActionableError(e.message)
      if (e.type === "InvitationNotFound")
        throw new InvitationNotFoundError(e.message)
      throw e
    })

export const getSession = () =>
  apiClient
    .get<SessionResponse>("/sign/session")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const logoutSession = () => apiClient.post<null>("/sign/session/logout")

export const getDocument = ({ document_id }: { document_id: number }) =>
  apiClient
    .get<SigningDocument>(`/sign/documents/${document_id}`)
    .catch((e) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getSignee = ({
  document_id,
  signee_id,
}: {
  document_id: number
  signee_id: number
}) =>
  apiClient
    .get<DocumentSignee>(`/sign/documents/${document_id}/signees/${signee_id}`)
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getEmailVerificationStatus = () =>
  apiClient
    .get<EmailVerificationStatus>("/sign/verify/email/status")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const sendVerificationCode = () =>
  apiClient
    .post<EmailVerificationStatus>("/sign/verify/email")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateEmailCode = ({ code }: { code: number }) =>
  apiClient
    .post<EmailVerificationStatus>("/sign/verify/email/validate", { code })
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateCardUpload = ({ file_id }: { file_id: number }) =>
  apiClient
    .post<CardIdVerificationStatus>("/sign/verify/card-id/validate", {
      file_id,
    })
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const uploadCardFile = ({
  kind,
  file,
}: {
  kind: "card_id_front" | "card_id_rear" | "video_confirmation"
  file: File
}) =>
  apiClient
    .post<{
      file_id: number
      upload_url: string
    }>("/sign/verify/card-id", { kind, mime_type: file.type })
    .then(async ({ file_id, upload_url }) => {
      // Cross-origin S3 PUT — must NOT include credentials (AWS rejects credentialed CORS).
      return fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      }).then(async (response) => {
        if (response.ok) {
          return validateCardUpload({ file_id })
        }

        let body
        try {
          body = await response.json()
        } catch (jsonError) {
          Sentry.captureException(jsonError, {
            tags: {
              errorType: "non_json_error_response",
              httpMethod: "PUT",
              operation: "file_upload",
            },
            extra: {
              url: upload_url,
              fileType: file.type,
              fileName: file.name,
              fileSize: file.size,
              status: response.status,
              statusText: response.statusText,
              headers: Object.fromEntries(response.headers.entries()),
              responseType: response.type,
              originalError: jsonError,
            },
          })

          body = {
            type: "NetworkError",
            message:
              "Ha ocurrido un error subiendo el archivo. Por favor, inténtalo de nuevo.",
          }
        }
        throw new ApiRequestError(body, response)
      })
    })
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getCardIdVerificationStatus = () =>
  apiClient
    .get<CardIdVerificationStatus>("/sign/verify/card-id/status")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getVideoVerificationStatus = () =>
  apiClient
    .get<VideoVerificationStatus>("/sign/verify/video/status")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateVideoUpload = ({ file_id }: { file_id: number }) =>
  apiClient
    .post<CardIdVerificationStatus>("/sign/verify/video/validate", { file_id })
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const uploadVideoFile = ({
  file,
  onUploadProgress,
}: {
  file: File
  onUploadProgress: (progress: number) => void
}) =>
  apiClient
    .post<{
      file_id: number
      upload_url: string
    }>("/sign/verify/video", {
      mime_type: file.type,
      kind: "video_confirmation",
    })
    .then(async ({ file_id, upload_url }) => {
      // Cross-origin S3 PUT — must NOT include credentials (AWS rejects credentialed CORS).
      const xhr = new XMLHttpRequest()
      return new Promise<{ file_id: number; uploaded: boolean }>((resolve) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const progress = event.loaded / event.total
            onUploadProgress(progress * 100)
          }
        })
        xhr.addEventListener("loadend", () => {
          resolve({
            file_id,
            uploaded: xhr.readyState === 4 && xhr.status === 200,
          })
        })
        xhr.open("PUT", upload_url, true)
        xhr.setRequestHeader("Content-Type", file.type)
        xhr.send(file)
      }).then((res) => {
        if (res.uploaded) {
          return validateVideoUpload({ file_id })
        }
      })
    })
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getNextVerificationStep = () =>
  apiClient
    .post<NextStepResponse>("/sign/verify/continue")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getDocumentFile = ({ document_id }: { document_id: number }) =>
  apiClient
    .get<GetDocumentFileResponse>(`/sign/documents/${document_id}/file`)
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const downloadDocumentFile = async ({
  document_id,
  filename,
}: {
  document_id: number
  filename: string
}) => {
  return getDocumentFile({ document_id }).then(async (data) => {
    return fetch(data.download_url, {
      method: "GET",
    })
      .then(async (response) => {
        if (response.ok) {
          return response.blob()
        }

        let body
        try {
          body = await response.json()
        } catch (jsonError) {
          Sentry.captureException(jsonError, {
            tags: {
              errorType: "non_json_error_response",
              httpMethod: "GET",
              operation: "document_download",
            },
            extra: {
              url: data.download_url,
              documentId: document_id,
              filename: filename,
              status: response.status,
              statusText: response.statusText,
              headers: Object.fromEntries(response.headers.entries()),
              responseType: response.type,
              originalError: jsonError,
            },
          })

          body = {
            type: "NetworkError",
            message:
              "Ha ocurrido un error descargando el documento. Por favor, inténtalo de nuevo.",
          }
        }
        throw new ApiRequestError(body, response)
      })
      .then((blob) => {
        return new File([blob], filename, {
          type: "application/pdf",
        })
      })
  })
}

export const uploadSignatureFile = async ({
  document_id,
  file,
}: {
  document_id: number
  file: File
}) =>
  apiClient
    .post<{ upload_url: string }>(`/sign/documents/${document_id}/signature`, {
      mime_type: file.type,
    })
    .then(async ({ upload_url }) =>
      // Cross-origin S3 PUT — must NOT include credentials (AWS rejects credentialed CORS).
      fetch(upload_url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      }),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const signDocument = async ({
  document_id,
  signature,
}: {
  document_id: number
  signature: File
}) =>
  uploadSignatureFile({ file: signature, document_id })
    .then(() =>
      apiClient
        .post(`/sign/documents/${document_id}/sign`)
        .catch((e: ApiRequestError) => {
          if (e.status === 401) throw new ExpiredTokenError()
          throw e
        }),
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getReservationOfRightsStatus = () =>
  apiClient
    .get<ReservationOfRightsStatus>("/sign/verify/rights/status")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateReservationOfRights = (data: {
  reservation_of_rights: boolean
  reservation_of_rights_content: string | null
}) =>
  apiClient
    .post<ReservationOfRightsStatus>("/sign/verify/rights/validate", {
      reservation_of_rights: data.reservation_of_rights,
      reservation_of_rights_content: data.reservation_of_rights_content,
    })
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateAttachments = () =>
  apiClient
    .post<null>("/sign/verify/attachments/validate")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const listSigneeAttachments = ({
  document_id,
  signee_id,
}: {
  document_id: number
  signee_id: number
}) =>
  apiClient
    .get<AttachmentItem[]>(
      `/sign/documents/${document_id}/signees/${signee_id}/attachments`,
    )
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getBankAccountsStatus = () =>
  apiClient
    .get<BankAccountStatus>("/sign/verify/bank-account/status")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const createBankAccount = ({
  holder_id,
  holder_name,
  number,
  type,
  bank_id,
}: BankAccountCreate) =>
  apiClient
    .put<BankAccountStatus>("/sign/verify/bank-account", {
      holder_id,
      holder_name,
      number,
      type,
      bank_id,
    })
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const validateBankAccount = () =>
  apiClient
    .post<BankAccountStatus>("/sign/verify/bank-account/validate")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getBanks = () =>
  apiClient.get<BankResponse[]>("/banks").catch((e: ApiRequestError) => {
    throw e
  })

// Payment API functions
export const getPaymentStatus = () =>
  apiClient
    .get<PaymentStatusResponse>("/sign/payment/status")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const createPaymentPreference = () =>
  apiClient
    .post<CreatePaymentPreferenceResponse>("/sign/payment/preference")
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })

export const getValidation = (token: string) =>
  apiClient.get<ValidationResponse>(`/validations/${encodeURIComponent(token)}`)

export const getValidationDownloadUrl = (token: string) =>
  `${API_BASE_URL}/validations/${encodeURIComponent(token)}/download`

export const processPayment = (body: ProcessPaymentRequest) =>
  apiClient
    .post<ProcessPaymentResponse>("/sign/payment/process", body)
    .catch((e: ApiRequestError) => {
      if (e.status === 401) throw new ExpiredTokenError()
      throw e
    })
